import { singleton } from "@core/foundation/class";
import { getBrandedString } from "@core/foundation/string";
import type { PK, SK } from "@core/ports/json-storage";
import { SK_VERSION_STRING } from "@core/ports/json-storage";
import type { ZodUnwrap, z } from "@core/zod";

import { BaseDeviceRepository } from "@react/ionic/storage";

import { BarcodeProduct } from "@apis/calorie-grid/models";

import { deviceStorage } from "../bootstrap/device-storage.ts";
import { TABLE_NAME } from "../bootstrap/storage.ts";

const identifierFields = { barcode: true } as const;

const BarcodeProductIdentifierSchema =
  BarcodeProduct.schema.pick(identifierFields);

type BarcodeProductIdentifier = z.infer<typeof BarcodeProductIdentifierSchema>;

const BarcodeProductDataSchema = BarcodeProduct.schema.omit(identifierFields);

class BarcodeProductRepository extends BaseDeviceRepository<
  ZodUnwrap<typeof BarcodeProductIdentifierSchema>,
  ZodUnwrap<typeof BarcodeProductDataSchema>,
  BarcodeProduct,
  "barcode-product"
> {
  public static readonly getInstance = singleton(
    () => new BarcodeProductRepository(),
  );

  private constructor() {
    super({
      dataSchema: BarcodeProductDataSchema,
      deviceStorage,
      entityType: "barcode-product",
      identifierSchema: BarcodeProductIdentifierSchema,
      readSchema: BarcodeProduct.readSchema,
      resourceName: "Barcode product",
      tableName: TABLE_NAME,
    });
  }

  protected getPK({
    barcode,
  }: Pick<BarcodeProductIdentifier, "barcode">): PK<typeof this.entityType> {
    return getBrandedString(`${this.entityType}#${barcode}`);
  }

  protected getSK(): SK {
    return getBrandedString(SK_VERSION_STRING.v0);
  }
}

export const barcodeProductRepository = BarcodeProductRepository.getInstance();
